import { generatePath, useLocation } from 'react-router-dom';

import { List } from '@uptime/design-system/components/List';
import { ListItemText } from '@uptime/design-system/components/ListItemText';
import AnalyticsIcon from '@uptime/design-system/icons/AnalyticsIcon';
import ClipboardCheckIcon from '@uptime/design-system/icons/ClipboardCheckIcon';
import ClipboardIcon from '@uptime/design-system/icons/ClipboardIcon';
import DeviceIcon from '@uptime/design-system/icons/DeviceIcon';
import FacilityIcon from '@uptime/design-system/icons/FacilityIcon';
import GroupIcon from '@uptime/design-system/icons/GroupIcon';
import TasksIcon from '@uptime/design-system/icons/TasksIcon';
import {
  ListItem,
  ListItemIcon,
  CollapsableListItem,
  CollapsableListSubItem,
} from '@uptime/shared/components/SidebarListItem';
import useSidebar from '@uptime/shared/hooks/useSidebar';

import { DashboardIDs } from '@/constants/dashboards';
import { Routes } from '@/constants/router';

interface Props {
  onNavigate: () => void;
}

const SidebarList = ({ onNavigate }: Props) => {
  const location = useLocation();
  const { isActive, toggle } = useSidebar();

  return (
    <List disablePadding>
      {/* Users */}
      <ListItem to={Routes.Users} onClick={onNavigate}>
        <ListItemIcon>
          <GroupIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Users" />
      </ListItem>

      {/* Facilities */}
      <ListItem to={Routes.Facilities} onClick={onNavigate}>
        <ListItemIcon>
          <FacilityIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Facilities" />
      </ListItem>

      {/* Devices */}
      <ListItem to={Routes.Devices} onClick={onNavigate}>
        <ListItemIcon>
          <DeviceIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Devices" />
      </ListItem>

      {/* Tasks */}
      <CollapsableListItem
        pathname={Routes.TasksRoot}
        isActive={isActive}
        onClick={toggle}
        main={
          <>
            <ListItemIcon active={location.pathname.startsWith(Routes.TasksRoot)}>
              <TasksIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Tasks" />
          </>
        }
      >
        <CollapsableListSubItem to={Routes.TasksBulkCreation} onClick={onNavigate}>
          <ListItemText primary="Bulk Task Creation" />
        </CollapsableListSubItem>
      </CollapsableListItem>

      {/* Service Requests */}
      <ListItem to={Routes.WorkOrders} onClick={onNavigate}>
        <ListItemIcon>
          <ClipboardIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Service Requests" />
      </ListItem>

      {/* Logs */}
      <CollapsableListItem
        pathname={Routes.LogsRoot}
        isActive={isActive}
        onClick={toggle}
        main={
          <>
            <ListItemIcon active={location.pathname.startsWith(Routes.LogsRoot)}>
              <ClipboardCheckIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logs" />
          </>
        }
      >
        <CollapsableListSubItem to={Routes.LogTypes} onClick={onNavigate}>
          <ListItemText primary="Log Types" />
        </CollapsableListSubItem>
        <CollapsableListSubItem to={Routes.LogsCompleted} onClick={onNavigate}>
          <ListItemText primary="Completed Logs" />
        </CollapsableListSubItem>
      </CollapsableListItem>

      {/* Analytics */}
      <CollapsableListItem
        pathname={Routes.AnalyticsRoot}
        isActive={isActive}
        onClick={toggle}
        main={
          <>
            <ListItemIcon active={location.pathname.startsWith(Routes.AnalyticsRoot)}>
              <AnalyticsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Analytics" />
          </>
        }
      >
        <CollapsableListSubItem
          to={generatePath(Routes.Analytics, { dashboardID: DashboardIDs.Activities })}
          onClick={onNavigate}
        >
          <ListItemText primary="Activities" />
        </CollapsableListSubItem>
        <CollapsableListSubItem
          to={generatePath(Routes.Analytics, { dashboardID: DashboardIDs.ServiceRequests })}
          onClick={onNavigate}
        >
          <ListItemText primary="Service Requests" />
        </CollapsableListSubItem>

        <CollapsableListSubItem
          to={generatePath(Routes.Analytics, { dashboardID: DashboardIDs.Devices })}
          onClick={onNavigate}
        >
          <ListItemText primary="Devices" />
        </CollapsableListSubItem>

        <CollapsableListSubItem
          to={generatePath(Routes.Analytics, { dashboardID: DashboardIDs.Facilities })}
          onClick={onNavigate}
        >
          <ListItemText primary="Facilities" />
        </CollapsableListSubItem>

        <CollapsableListSubItem
          to={generatePath(Routes.Analytics, { dashboardID: DashboardIDs.Financials })}
          onClick={onNavigate}
        >
          <ListItemText primary="Financials" />
        </CollapsableListSubItem>

        <CollapsableListSubItem
          to={generatePath(Routes.Analytics, { dashboardID: DashboardIDs.WarrantyAndContracts })}
          onClick={onNavigate}
        >
          <ListItemText primary="Warranty & Contracts" />
        </CollapsableListSubItem>

        <CollapsableListSubItem
          to={generatePath(Routes.Analytics, { dashboardID: DashboardIDs.Users })}
          onClick={onNavigate}
        >
          <ListItemText primary="Users" />
        </CollapsableListSubItem>
        <CollapsableListSubItem
          to={generatePath(Routes.Analytics, { dashboardID: DashboardIDs.Logs })}
          onClick={onNavigate}
        >
          <ListItemText primary="Logs" />
        </CollapsableListSubItem>
      </CollapsableListItem>
    </List>
  );
};

export default SidebarList;
